import React from 'react';
import styled from 'styled-components';
import { map } from 'styled-components-breakpoint';
import { p, pt, px, py, pb, mb, mt } from 'styled-components-spacing';

import { colors, Text, Heading } from 'themes/components';
import HighlightText from 'utils/HighlightText';
import GlowBorder from 'components/GlowBorder';
import CleanBuildIcon from './icons/CleanBuildIcon';
import ApiIcon from './icons/ApiIcon';
import AccessIcon from './icons/AccessIcon';
import SyncIcon from './icons/SyncIcon';

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  ${px(2)};
  ${pt({ mobile: 5 })};
  ${mb({ mobile: 5, desktop: 8 })};
  position: relative;
`;

const TextContent = styled.div`
  max-width: 600px;
  text-align: center;
  margin: 0 auto;
  ${mt(3)};
`;

const TextContainer = styled.div`
  margin: 0 auto;
  color: ${colors.grey20};
  ${map(
    {
      mobile: '24px',
      desktop: '0'
    },
    (mb) => `margin-bottom: ${mb};`
  )};
`;

const BenefitsGrid = styled.div`
  display: grid;
  ${mt(4)};
  ${map(
    {
      mobile: '1fr',
      tablet: '1fr 1fr'
    },
    (cols) => `grid-template-columns: ${cols};`
  )};
  gap: 12px;
`;

const BenefitItem = styled.div`
  background-color: ${colors.grey80};
  border: 1px solid ${colors.grey70};
  ${px({ mobile: 3, tablet: 5 })};
  ${py({ mobile: 2, tablet: 4 })};
  border-radius: 6px;
  font-size: 14px;
`;

const BenefitTitle = styled.h3`
  color: ${colors.grey5};
  font-weight: 600;
  margin-top: 0;
  ${mb(0)};
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const BenefitDescription = styled(Text)`
  color: ${colors.grey30};
  ${mb(1)};
`;

const BenefitLink = styled.a`
  background: linear-gradient(68deg, #7cfadb 0.54%, #54d472 99.37%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  text-decoration: none;
  transition: transform 0.2s ease-in-out;
  font-weight: 500;
  display: inline-block;
  &:hover {
    transform: translateX(4px);
  }
`;

const Tag = styled.span`
  font-size: 12px;
  font-weight: 500;
  padding: 4px 8px;
  background: linear-gradient(
    to top left,
    hsla(350, 100%, 88%, 0.1),
    hsla(300, 100%, 25%, 0.1)
  );
  border-radius: 6px;
  border: 1px solid ${colors.pink40}15;
`;

const EnterprisePlan = () => (
  <Tag>
    <HighlightText
      direction="right"
      stopOne={colors.pink40}
      stopTwo={colors.purple30}
    >
      Enterprise plan
    </HighlightText>
  </Tag>
);

const Benefits = () => (
  <Container>
    <GlowBorder
      borderColor="hsla(265, 48%, 53%, 0.4)"
      glow1="hsla(197, 100%, 75%, 0.38)"
      glow2="hsla(279, 68%, 52%, 0.52)"
      glow3="hsla(302, 95%, 85%, 0.4)"
    />
    <TextContent>
      <Heading variant="h2" style={{ color: colors.heading }}>
        Seamless integration&nbsp;
        <HighlightText
          direction="right"
          stopOne={colors.purple30}
          stopTwo={colors.blue30}
        >
          into your existing workflows
        </HighlightText>
      </Heading>
      <TextContainer style={{ marginTop: '16px' }}>
        <Text>
          Mudstack fits into your existing workflows. Our custom content
          migration and rapid implementation means your team can get going in
          days, not months.
        </Text>
      </TextContainer>
    </TextContent>

    <BenefitsGrid>
      <BenefitItem>
        <BenefitTitle>
          <CleanBuildIcon fillColor={colors.purple30} /> Protect your build
          system
        </BenefitTitle>
        <BenefitDescription>
          Mudstack manages and version controls source assets, so you can keep
          your build system clean and focused on code and final game-ready
          assets.
        </BenefitDescription>
        <BenefitLink
          href="https://docs.mudstack.com/features/version-control"
          target="_blank"
        >
          Learn more about artist-friendly version control →
        </BenefitLink>
      </BenefitItem>

      <BenefitItem>
        <BenefitTitle>
          <ApiIcon fillColor={colors.purple30} /> Robust API access
          <EnterprisePlan />
        </BenefitTitle>
        <BenefitDescription>
          Connect Mudstack with your existing tools and systems. Our robust API
          allows you to automate workflows and integrate with your tech stack.
        </BenefitDescription>
        <BenefitLink href="mailto:sales@mudstack.com" target="_blank">
          Request API access →
        </BenefitLink>
      </BenefitItem>

      <BenefitItem>
        <BenefitTitle>
          <AccessIcon fillColor={colors.purple30} /> Advanced access controls
          <EnterprisePlan />
        </BenefitTitle>
        <BenefitDescription>
          Fine-grained permissions ensure that artists only see what they need
          to see. Maintain security without compromising productivity.
        </BenefitDescription>
        <BenefitLink
          href="https://docs.mudstack.com/getting-started/permissions"
          target="_blank"
        >
          Learn how to manage access and permissions →
        </BenefitLink>
      </BenefitItem>

      <BenefitItem>
        <BenefitTitle>
          <SyncIcon fillColor={colors.purple30} /> Fast file synchronization
        </BenefitTitle>
        <BenefitDescription>
          Ensure that your team always has access to the latest versions of
          assets without slowing down production pipelines. Manage massive file
          transfers efficiently.
        </BenefitDescription>
        <BenefitLink
          href="https://docs.mudstack.com/features/sync-overview"
          target="_blank"
        >
          See how file synchronization works →
        </BenefitLink>
      </BenefitItem>
    </BenefitsGrid>
  </Container>
);

export default Benefits;
